/**
 * hfb-reveal — the single scroll-reveal chokepoint for the Firewood Factory theme.
 *
 * Zero dependencies, ~0ms on the critical path: a custom element that observes its
 * own children marked .hfb-reveal with one shared IntersectionObserver and toggles
 * .is-in (the CSS in assets/hfb.css does the transition). Honors prefers-reduced-
 * motion by revealing immediately. Also drives the stamped-X path-draw signature
 * (elements marked [data-hfb-draw]) and the spec-numeral count-up ([data-hfb-count]).
 */
const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HfbReveal extends HTMLElement {
  connectedCallback() {
    const targets = this.querySelectorAll('.hfb-reveal, [data-hfb-draw], [data-hfb-count]');

    if (REDUCE || !('IntersectionObserver' in window)) {
      // Motion disabled: leave content visible (CSS default), just settle draw/count.
      for (const el of targets) {
        el.classList.add('is-in');
        this.#finishDraw(el);
        this.#finishCount(el);
      }
      return;
    }

    // Opt IN to the hidden pre-state only now that JS is running, so a no-JS render
    // stays visible. This class is what the CSS uses to hide .hfb-reveal descendants.
    this.classList.add('hfb-reveal-ready');

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          el.classList.add('is-in');
          this.#startDraw(el);
          this.#startCount(el);
          obs.unobserve(el);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    for (const el of targets) observer.observe(el);
  }

  #startDraw(el) {
    if (!el.hasAttribute('data-hfb-draw')) return;
    const paths = el.querySelectorAll('path, line, polyline');
    for (const p of paths) {
      const len = p.getTotalLength ? p.getTotalLength() : 0;
      if (!len) continue;
      p.style.transition = 'none';
      p.style.strokeDasharray = String(len);
      p.style.strokeDashoffset = String(len);
      // force reflow, then animate the stroke in
      void p.getBoundingClientRect();
      p.style.transition = 'stroke-dashoffset 900ms cubic-bezier(0.16,0.84,0.28,1)';
      p.style.strokeDashoffset = '0';
    }
  }

  #finishDraw(el) {
    if (!el.hasAttribute('data-hfb-draw')) return;
    for (const p of el.querySelectorAll('path, line, polyline')) {
      p.style.strokeDashoffset = '0';
    }
  }

  #startCount(el) {
    if (!el.hasAttribute('data-hfb-count')) return;
    const target = parseFloat(el.getAttribute('data-hfb-count'));
    if (Number.isNaN(target)) return;
    const suffix = el.getAttribute('data-hfb-count-suffix') || '';
    const duration = 900;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = Math.round(target * eased) + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  #finishCount(el) {
    if (!el.hasAttribute('data-hfb-count')) return;
    const target = el.getAttribute('data-hfb-count');
    const suffix = el.getAttribute('data-hfb-count-suffix') || '';
    el.textContent = target + suffix;
  }
}

if (!customElements.get('hfb-reveal')) {
  customElements.define('hfb-reveal', HfbReveal);
}
